import { Activity, Target, TrendingUp, CornerDownRight } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import type { MatchStatsPayload } from "@/hooks/useMatchInsights";
import {
  requiresPreMatchPlaceholder,
  type MatchLifecycle,
  type OfficialFootballKey,
} from "@/lib/dataPolicy";

interface QuickStatsProps {
  /** Match lifecycle so we never fabricate live stats. */
  lifecycle?: MatchLifecycle;
  /** Real stats payload from the API-Football provider (via useMatchInsights). */
  stats?: MatchStatsPayload;
  /** True while the provider request is in flight. */
  isLoading?: boolean;
  /** True when the provider errored — surfaces a graceful placeholder. */
  isError?: boolean;
  homeCode?: string;
  awayCode?: string;
}

function readNum(
  stats: MatchStatsPayload | undefined,
  key: string,
): number | undefined {
  if (!stats) return undefined;
  const v = stats[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * Real Match Data Phase 1 — QuickStats is now driven exclusively by the
 * official API-Football fixture statistics endpoint. Pre-match renders
 * placeholders (per `dataPolicy.requiresPreMatchPlaceholder`); LIVE and
 * FINISHED render only official values; provider errors show a graceful
 * placeholder. No fabricated numbers.
 */
export function QuickStats({
  lifecycle = "LIVE",
  stats,
  isLoading = false,
  isError = false,
  homeCode = "HOME",
  awayCode = "AWAY",
}: QuickStatsProps) {
  const { t } = useLanguage();

  const possessionHome = readNum(stats, "possessionHome");
  const possessionAway = readNum(stats, "possessionAway");
  const shotsHome = readNum(stats, "shotsHome");
  const shotsAway = readNum(stats, "shotsAway");
  const xgHome = readNum(stats, "xgHome");
  const xgAway = readNum(stats, "xgAway");
  const cornersHome = readNum(stats, "cornersHome");
  const cornersAway = readNum(stats, "cornersAway");

  type Row = {
    key: OfficialFootballKey | "xg";
    label: string;
    home: number | undefined;
    away: number | undefined;
    suffix?: string;
    fractionDigits?: number;
    icon: typeof Activity;
    color: string;
  };

  const rows: Row[] = [
    { key: "possession", label: t("stat_possession"), home: possessionHome, away: possessionAway, suffix: "%", icon: Activity, color: "oklch(0.82 0.24 145)" },
    { key: "shots", label: t("stat_shots"), home: shotsHome, away: shotsAway, icon: Target, color: "oklch(0.7 0.2 50)" },
    { key: "xg", label: t("stat_xg"), home: xgHome, away: xgAway, fractionDigits: 2, icon: TrendingUp, color: "oklch(0.65 0.22 280)" },
    { key: "corners", label: t("stat_corners"), home: cornersHome, away: cornersAway, icon: CornerDownRight, color: "oklch(0.58 0.24 27)" },
  ];

  const fmt = (n: number | undefined, r: Row): string => {
    if (n === undefined) return "—";
    const s = r.fractionDigits != null ? n.toFixed(r.fractionDigits) : String(n);
    return r.suffix ? `${s}${r.suffix}` : s;
  };

  // Aggregate policy decision: does any tile actually have a real value?
  const hasAnyValue = rows.some((r) => r.home !== undefined || r.away !== undefined);

  // Placeholder message when policy forbids fabrication and there is no data.
  let notice: string | null = null;
  if (!hasAnyValue && !isLoading) {
    if (lifecycle === "SCHEDULED") notice = t("stat_prematch_none");
    else notice = t("stat_waiting_official");
  }
  if (isError && !hasAnyValue) notice = t("stat_waiting_official");

  return (
    <div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {rows.map((r) => {
          // Never fabricate: if the provider has no value and the policy
          // forbids fake numbers for this key, always render an em dash.
          // xG shares the same policy as shots — treat as shots for the guard.
          const policyKey: OfficialFootballKey = r.key === "xg" ? "shots" : r.key;
          const forbid = requiresPreMatchPlaceholder(
            policyKey,
            lifecycle,
            r.home !== undefined || r.away !== undefined,
          );
          const homeVal = forbid ? "—" : fmt(r.home, r);
          const awayVal = forbid ? "—" : fmt(r.away, r);
          // Progress bar reflects real home possession-share when possession
          // is the tile; otherwise a neutral 0 (no fabricated momentum).
          const barPct =
            r.key === "possession" && typeof r.home === "number"
              ? Math.max(0, Math.min(100, r.home))
              : 0;
          return (
            <div key={r.key} className="rounded-2xl glass p-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {r.label}
                </span>
                <r.icon className="h-3.5 w-3.5" style={{ color: r.color }} />
              </div>
              <div className="mt-1.5 flex items-baseline gap-1">
                <span className="text-xl font-black tabular-nums" style={{ color: r.color }}>
                  {homeVal}
                </span>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {t("stat_vs")} {awayVal}
                </span>
              </div>
              <div className="mt-1 flex justify-between text-[9px] uppercase tracking-wider text-muted-foreground/70">
                <span>{homeCode}</span>
                <span>{awayCode}</span>
              </div>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-accent/40">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${barPct}%`, background: r.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {notice ? (
        <p className="mt-2 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
